<template>
    <div class="da-table" :dir="dir">
        <div v-if="rows.length === 0">
            <div class="nodata" style="padding: 10px 0; color: #7f849a; text-align: center">
                {{ "No delivery areas" }}

                <button
                    type="button"
                    @click="insertRow(0)"
                    class="btn btn-success btn-add-delivery-area p-2 ms-2"
                    style="margin-start: 8px"
                >
                    <i class="la la-plus p-0"></i>
                </button>
            </div>
        </div>
        <div v-if="rows.length != 0" class="da-scroll">
            <table>
                <thead>
                    <tr>
                        <th>{{ translations.deliveryAddress.postalCode }} <span class="mandatory">*</span></th>
                        <th>{{ translations.deliveryAddress.minOrderSize }}</th>
                        <th>{{ translations.deliveryAddress.deliveryFee }}</th>
                        <th>{{ translations.deliveryAddress.freeDeliveryFrom }}</th>
                        <th class="da-col-add" aria-hidden="true"></th>
                    </tr>
                </thead>

                <tbody>
                    <DeliveryAreaRow
                        v-for="(row, index) in rows"
                        :row="row"
                        :index="index"
                        @removeRow="removeRow"
                        @insert-row="insertRow"
                        :key="row.id"
                        :ref="(el) => setRowRef(el, row.id)"
                        class="da-row"
                    />
                </tbody>
                <tfoot>
                    <tr>
                        <td style="padding: 0 12px 10px 12px; max-width: 300px">
                            <span class="form-text text-muted">{{
                                translations.deliveryAddress.postalCodeRangeHint
                            }}</span>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IDeliveryArea, IDeliveryAreaToEdit } from "@/data/model/Customer";
    import { translations } from "@/shared/localization/translations";
    import { v4 as uuidv4 } from "uuid";
    import { computed, reactive, ref } from "vue";

    import DeliveryAreaRow from "@/portal/forms/DeliveryAreaRow.vue";

    const dir = computed(() => "ltr");
    const rowRefs = ref<Record<string, typeof DeliveryAreaRow>>({});

    function setRowRef(row, id) {
        if (row) {
            rowRefs.value[id] = row;
        } else {
            delete rowRefs.value[id];
        }
    }

    function emptyRow(): IDeliveryAreaToEdit {
        return reactive({
            id: uuidv4(),
            deliveryFee: undefined,
            minimumOrderSize: undefined,
            postalCodes: "",
            freeDeliveryFrom: undefined,
        });
    }

    const rows = reactive<Array<IDeliveryAreaToEdit>>([]);

    // ---- add / remove rows -------------------------------------------------

    function insertRow(index) {
        rows.splice(index + 1, 0, emptyRow());
    }

    function removeRow(index) {
        if (rows.length === 0) return;
        rows.splice(index, 1);
    }

    function validateRows(): boolean {
        let hasErrors = false;

        Object.values(rowRefs.value).forEach((r) => {
            console.log("VALIDATE");
            if (r.validate() === false) {
                console.log("HAS ERROR");
                hasErrors = true;
            }
        });

        return !hasErrors;
    }

    function getValidRows(): Array<IDeliveryArea> {
        let validRows = Array<IDeliveryAreaToEdit>();

        Object.values(rowRefs.value).forEach((r) => {
            if (r.validate() === true) {
                validRows.push(r.getRow());
            }
        });

        return validRows.map((r) => {
            return {
                id: r.id,
                deliveryFee: r.deliveryFee,
                minimumOrderSize: r.minimumOrderSize,
                postalCodes: r.postalCodes!,
                freeDeliveryFrom: r.freeDeliveryFrom,
            };
        });
    }

    function setRows(deliveryAreas: Array<IDeliveryArea>) {
        rows.splice(0, rows.length);

        deliveryAreas.forEach((deliveryArea: IDeliveryArea) => {
            rows.push(deliveryArea);
        });
    }

    defineExpose({
        validateRows,
        getValidRows,
        setRows,
    });
</script>

<style scoped>
    .da-table {
        --da-border: #dcdfe4;
        --da-text: #1f2430;
        --da-muted: #6b7280;
        --da-bg-head: #f2f3f8;
        color: var(--da-text);
    }

    .da-scroll {
        overflow-x: auto;
        border: 1px solid var(--da-border);
        border-radius: 0.25rem;
    }

    table {
        border-collapse: collapse;
        width: 100%;
        min-width: 760px;
    }

    thead th {
        text-align: left;
        font-size: 1rem;
        font-weight: 600;
        letter-spacing: 0.01em;
        color: var(--da-muted);
        background: var(--da-bg-head);
        padding: 10px 12px;
        border-bottom: 1px solid var(--da-border);
        white-space: nowrap;
    }

    .da-row td {
        border-bottom: 1px solid var(--da-border);
        padding: 10px 12px;
        vertical-align: top;
    }

    .da-row:last-child td {
        border-bottom: none;
    }

    .da-col-add {
        width: 40px;
        padding: 10px 4px !important;
        text-align: center;
        white-space: nowrap;
    }

    .btn-add-delivery-area {
        margin-start: 12px;
    }
</style>
